/**
 * ReferenceChip — Pill-shaped thumbnail with label, role color, and close button.
 *
 * Generic primitive for media references, including image thumbnails and
 * optional custom fallback-thumb content. Does not know about roles or modes —
 * the parent maps domain concepts to props.
 *
 * @param {string} src - Thumbnail URL or data URI
 * @param {string} label - Display text ("Subject", "Style", "First", "Last", etc.)
 * @param {string|null} roleColor - CSS color for accent stripe, null = neutral
 * @param {function|null} onClick - Click handler for cycling (null = no cycling)
 * @param {function} onRemove - Close button callback
 * @param {boolean} disabled - Disables interaction
 * @param {boolean} dimmed - Over-limit visual (reduced opacity)
 * @param {'default'|'compact'} size - Visual density variant
 * @param {string} className - Additional CSS classes
 * @param {React.ReactNode|null} thumbContent - Custom fallback content when src is absent
 * @param {'pending'|'working'|'ready'|null} status - Renders one status dot. Its meaning is
 *   the caller's: the host-reference chip uses it for whether the scene clip is cached.
 *   Absent on every ordinary reference chip, which renders no dot at all.
 * @param {boolean} labelLoading - Render the label through the shared LoadingText shimmer,
 *   for a chip whose work is in flight ("Exporting…").
 */
import { forwardRef } from 'react';
import LoadingText from './LoadingText';
import '../../styles/reference-chip.css';

const CloseIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ReferenceChip = forwardRef(function ReferenceChip({
  src,
  label,
  roleColor = null,
  onClick = null,
  onRemove = null,
  disabled = false,
  dimmed = false,
  size = 'default',
  className = '',
  thumbContent = null,
  showThumbnail = true,
  status = null,
  labelLoading = false,
  style: customStyle,
  onKeyDown: onKeyDownProp,
  role: rootRole,
  tabIndex: rootTabIndex,
  ...containerProps
}, forwardedRef) {
  const chipClass = [
    'reference-chip',
    onClick && !disabled ? 'reference-chip--clickable' : '',
    dimmed ? 'reference-chip--dimmed' : '',
    size === 'compact' ? 'reference-chip--compact' : '',
    !showThumbnail ? 'reference-chip--no-thumb' : '',
    className,
  ].filter(Boolean).join(' ');

  const handleKeyDown = (event) => {
    onKeyDownProp?.(event);

    if (event.defaultPrevented || !onClick || disabled) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      ref={forwardedRef}
      className={chipClass}
      style={{
        ...(roleColor ? {
          '--reference-chip-label-color': roleColor,
          '--reference-chip-label-weight': 700,
        } : {}),
        ...customStyle,
      }}
      onClick={onClick && !disabled ? onClick : undefined}
      role={rootRole ?? (onClick ? 'button' : undefined)}
      tabIndex={rootTabIndex ?? (onClick && !disabled ? 0 : undefined)}
      onKeyDown={onClick || onKeyDownProp ? handleKeyDown : undefined}
      aria-disabled={disabled ? 'true' : undefined}
      {...containerProps}
    >
      {showThumbnail && (src ? (
        <img
          className="reference-chip__thumb"
          src={src}
          alt={label}
          draggable={false}
        />
      ) : (
        <div className="reference-chip__thumb reference-chip__thumb--fallback" aria-hidden="true">
          {thumbContent ?? (label?.trim()?.charAt(0)?.toUpperCase() || '?')}
        </div>
      ))}
      {status ? (
        <span className="reference-chip__status-dot" data-status={status} aria-hidden="true" />
      ) : null}
      {labelLoading ? (
        <LoadingText className="reference-chip__label">{label}</LoadingText>
      ) : (
        <span className="reference-chip__label">{label}</span>
      )}
      {typeof onRemove === 'function' && (
        <span className="reference-chip__close-slot">
          <button
            type="button"
            className="reference-chip__close"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            disabled={disabled}
            aria-label={`Remove ${label}`}
          >
            <CloseIcon />
          </button>
        </span>
      )}
    </div>
  );
});

export default ReferenceChip;
